<script setup lang="ts">
import {
  Check as CheckIcon,
  Compass as CompassIcon,
  CornerDownLeft as CornerDownLeftIcon,
  GalleryVerticalEnd as GalleryVerticalEndIcon,
  MapPin as MapPinIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Undo2 as Undo2Icon,
} from '@lucide/vue'

import type { useSmartSearch } from '@/composables/useSmartSearch'
import { useAuthStore } from '@/stores/authStore'

// Shared search results list, rendered inside the desktop pill dropdown (MapSmartSearch) and the
// mobile SearchSheet. The owning surface creates the `useSmartSearch()` instance and passes it in,
// so result rendering lives in exactly one place.
const props = defineProps<{ search: ReturnType<typeof useSmartSearch> }>()

const authStore = useAuthStore()

const {
  activeIndex,
  activateResult,
  displayedResults,
  favoriteActionLabel,
  handleFavoriteButtonClick,
  hasDefaultFavoriteResults,
  hasLiveMapQuery,
  isFavoriteMutationPending,
  isLocalLoading,
  isLocationResult,
  isPlaceLoading,
  isResultFavorited,
  isShowingPlaceResults,
  localError,
  placeError,
  query,
  setResultButtonRef,
  showLocalResults,
} = props.search
</script>

<template>
  <div class="overflow-x-hidden">
    <div
      v-if="isShowingPlaceResults"
      class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-3 py-3"
    >
      <div class="min-w-0">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
          Map results
        </p>
        <p class="text-sm text-gray-700">Choose a place and jump the map there.</p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white/90 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 hover:cursor-pointer"
        @click="showLocalResults"
      >
        <Undo2Icon class="h-4 w-4" />
        Back to app results
      </button>
    </div>

    <div
      v-if="localError || (isShowingPlaceResults && placeError)"
      class="px-3 py-3 text-sm text-red-700"
    >
      {{ placeError || localError }}
    </div>

    <div
      v-else-if="
        (query.trim() && isLocalLoading && displayedResults.length === 0) ||
        (isShowingPlaceResults && isPlaceLoading) ||
        (!isShowingPlaceResults &&
          hasLiveMapQuery &&
          isPlaceLoading &&
          displayedResults.length === 0)
      "
      class="px-3 py-8 text-center text-sm text-gray-500"
    >
      Searching...
    </div>

    <div v-else-if="displayedResults.length === 0 && query.trim()" class="px-3 py-8 text-center">
      <p class="text-base font-medium text-gray-900">No matches for "{{ query }}".</p>
      <p class="mt-2 text-sm text-gray-500">Try a broader name or place query.</p>
    </div>

    <div v-else-if="displayedResults.length > 0">
      <div
        v-if="!query.trim() && hasDefaultFavoriteResults"
        class="border-b border-gray-200 px-3 py-3 text-sm text-gray-700"
      >
        <p class="font-medium text-gray-900">Favorite places</p>
        <p class="mt-1">Saved places load first and stay searchable like any other result.</p>
      </div>

      <div
        v-for="(result, index) in displayedResults"
        :key="result.id"
        class="flex items-stretch gap-0 border-b border-gray-200 last:border-b-0"
        @mouseenter="activeIndex = index"
      >
        <button
          :ref="(element) => setResultButtonRef(element, index)"
          type="button"
          class="flex min-w-0 flex-1 items-start gap-3 px-3 py-3 text-left transition hover:cursor-pointer"
          :class="index === activeIndex ? 'bg-emerald-50/80' : 'hover:bg-emerald-50/35'"
          @focus="activeIndex = index"
          @click="activateResult(result)"
        >
          <span class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
            <SearchIcon v-if="result.kind === 'map-search'" class="h-4 w-4 text-emerald-700" />
            <GalleryVerticalEndIcon
              v-else-if="result.kind === 'workspace'"
              class="h-4 w-4 text-sky-600"
            />
            <CompassIcon
              v-else-if="result.kind === 'workspace-tour' || result.kind === 'library-tour'"
              class="h-4 w-4 text-amber-600"
            />
            <RouteIcon
              v-else-if="result.kind === 'workspace-track'"
              class="h-4 w-4 text-violet-600"
            />
            <StarIcon
              v-else-if="result.kind === 'favorite-place'"
              class="h-4 w-4 fill-amber-400 text-amber-500"
            />
            <MapPinIcon v-else class="h-4 w-4 text-rose-600" />
          </span>

          <span class="min-w-0 flex-1">
            <span class="flex items-center gap-2">
              <span class="truncate text-sm font-medium text-gray-900">{{ result.title }}</span>
              <CheckIcon
                v-if="index === activeIndex"
                class="h-3.5 w-3.5 shrink-0 text-emerald-600"
              />
              <span
                v-if="result.kind === 'favorite-place'"
                class="rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-amber-700"
              >
                Saved
              </span>
            </span>
            <span class="mt-1 block truncate text-xs text-gray-500">{{ result.subtitle }}</span>
          </span>

          <span
            class="hidden shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-white/90 px-2 py-1 text-[11px] text-gray-500 sm:inline-flex"
          >
            <CornerDownLeftIcon class="h-3 w-3" />
            Open
          </span>
        </button>

        <button
          v-if="authStore.isAuthenticated && isLocationResult(result)"
          type="button"
          class="inline-flex shrink-0 items-center justify-center border-l border-gray-200 px-3 py-3 text-gray-500 transition hover:bg-amber-50 hover:text-amber-600 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          :aria-label="favoriteActionLabel(result)"
          :disabled="isFavoriteMutationPending(result)"
          @click.stop="handleFavoriteButtonClick(index, result)"
        >
          <StarIcon
            class="h-4 w-4"
            :class="isResultFavorited(result) ? 'fill-amber-400 text-amber-500' : ''"
          />
        </button>
      </div>
    </div>
  </div>
</template>
